import {Button, Card, Col, Row, Select, Table, Tag} from "antd"
import {useDispatch, useSelector} from "react-redux"
import {StateType} from "../../store/store"
import React, {useCallback, useEffect, useMemo} from "react"
import {getCompanies, setCompanies, setCompaniesFilter, setWSCompany} from "../../store/companyReducer"
import {Loading} from "../../components/Loading/Loading"
import {filterFromString} from "../../helps/filterFromString"
import {useHistory, useLocation} from "react-router-dom"
import deepEqual from "deep-equal"
import {filterToString} from "../../helps/filterToString"
import {center} from "../../helps/tableHelper"
import {Helmet} from "react-helmet-async"
import {isActiveOptionsSelect, limitOptionsSelect} from "../../helps/optionsSelect"
import {FormSelect} from "../../components/Form/FormSelect"
import {FormInput} from "../../components/Form/FormInput"

const columns = [
    {
        title: 'Название',
        dataIndex: 'name',
        key: 'name',
        align: center,
    },
    {
        title: 'Статус',
        dataIndex: 'active',
        key: 'active',
        render: (active: boolean) => {
            return (
                <Tag color={active ? 'green' : 'volcano'} style={{marginRight: 0}}>{active ? 'Активно' : 'Не активно'}</Tag>
            )
        },
        align: center,
    },
]

export const Companies = () => {
    const dispatch = useDispatch()
    const location = useLocation()
    const history = useHistory()
    const companies = useSelector((state: StateType) => state.companyReducer.companies)
    const filter = useSelector((state: StateType) => state.companyReducer.filter)
    const ws = useSelector((state: StateType) => state.companyReducer.ws)
    const controller = useMemo(() => new AbortController(), [])

    useEffect(() => {
        const filterString = filterFromString(location.search)
        if (filterString && !deepEqual(filterString, filter)) {
            dispatch(setCompaniesFilter({...filterString}))
        } else if (location.search !== filterToString(filter)) {
            history.replace({search: filterToString(filter)})
        }
    }, [dispatch, filter, history, location])

    const getCompaniesFunc = useCallback((withoutLoadingIndicator = false) => {
        if (location?.search) {
            dispatch(getCompanies({
                ...filterFromString(location.search),
                withoutLoadingIndicator,
                controller,
            }))
        }
    }, [dispatch, location, controller])

    useEffect(() => {
        getCompaniesFunc()

        return () => {
            dispatch(setCompanies(null))
        }
    }, [dispatch, getCompaniesFunc])

    useEffect(() => {
        return () => {
            controller.abort()
        }
    }, [dispatch, controller])

    useEffect(() => {
        if (ws) {
            getCompaniesFunc(true)
        }
        dispatch(setWSCompany(null))
    }, [dispatch, ws, getCompaniesFunc])

    const data = useMemo(() => companies?.data?.map(i => {
        return {
            key: i.id,
            id: i.id,
            name: i.name,
            active: i.active,
        }
    }), [companies])

    return (
        <div className="tabled">
            <Helmet>
                <title>Компании</title>
            </Helmet>
            <Row gutter={[24, 0]}>
                <Col xs={24}>
                    <Card variant="borderless"
                          className="criclebox mb-24"
                          title={
                              <div className='table-title'>
                                  <div>Компании</div>
                                  <Button onClick={() => history.push('/companyForm')} type="link" size="middle" style={{marginTop: 5}}>
                                      Добавить
                                  </Button>
                              </div>
                          }
                          extra={
                              <div className="table-title-extra">
                                  <FormInput placeholder="Поиск"
                                             containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                             value={filter?.search}
                                             name="search"
                                             onChange={(e) => history.replace({search: filterToString({...filter, search: e.target.value, offset: 0})})}
                                             allowClear
                                  />

                                  <FormSelect value={filter?.status}
                                              containerStyle={{marginBottom: 0, width: 'auto', minWidth: 100}}
                                              dropdownStyle={{minWidth: 'min-content'}}
                                              onChange={(value) => history.replace({search: filterToString({...filter, status: value, offset: 0})})}
                                              options={isActiveOptionsSelect}
                                              placeholder="Статус"
                                              allowClear
                                              placement="bottomRight"
                                  />

                                  <FormSelect value={filter?.limit}
                                              containerStyle={{marginBottom: 0, width: 'auto'}}
                                              dropdownStyle={{minWidth: 'min-content'}}
                                              onChange={(value) => history.replace({search: filterToString({...filter, limit: value, offset: 0})})}
                                              options={limitOptionsSelect}
                                              placement="bottomRight"
                                  />
                              </div>
                          }
                    >
                        <div className="table-responsive">
                            {
                                companies
                                    ? <>
                                        <Table columns={columns}
                                               dataSource={data}
                                               pagination={filter?.limit ? {
                                                   position: companies?.data?.length >= 10 ? ['bottomLeft', 'topLeft'] : ['bottomLeft'],
                                                   pageSize: filter.limit,
                                                   hideOnSinglePage: true,
                                                   total: companies.total,
                                                   current: filter?.offset / filter?.limit + 1,
                                                   onChange: (page, pageSize) => {
                                                       const obj = {
                                                           limit: pageSize,
                                                           offset: (page - 1) * pageSize,
                                                       }
                                                       history.replace({search: filterToString({...filter, ...obj})})
                                                   },
                                                   showSizeChanger: false,
                                               } : false}
                                               className="ant-border-space"
                                               onRow={({id}) => ({
                                                   onClick: () => history.push(`/company/${id}`),
                                                   onMouseDown: (e) => e.button === 1 && window.open(`/company/${id}`, '_blank', 'noopener,noreferrer')
                                               })}
                                        />
                                    </>
                                    : <Loading/>
                            }
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    )
}
